

import { FocusMonitor } from '@angular/cdk/a11y';
import { Direction, Directionality } from '@angular/cdk/bidi';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  Directive,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  QueryList,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewEncapsulation
} from '@angular/core';
import { merge, Subject } from 'rxjs';
import { distinctUntilChanged, map, mergeMap, startWith, switchMap, takeUntil } from 'rxjs/operators';

import { MwcFormNoStatusService, MwcFormStatusService } from 'mwc/core/form';
import { BooleanInput, NgClassInterface, MwcSizeLDSType, MwcStatus, MwcValidateStatus } from 'mwc/core/types';
import { getStatusClassNames, InputBoolean } from 'mwc/core/util';

import { MwcInputDirective } from './input.directive';

@Directive({
  selector: `mwc-input-group[mwcSuffix], mwc-input-group[mwcPrefix]`
})
export class MwcInputGroupWhitSuffixOrPrefixDirective {
  constructor(public elementRef: ElementRef) {}
}

@Component({
  selector: 'mwc-input-group',
  exportAs: 'mwcInputGroup',
  preserveWhitespaces: false,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MwcFormNoStatusService],
  template: `
    <span class="mwc-input-wrapper mwc-input-group" *ngIf="isAddOn; else noAddOnTemplate">
      <span
        *ngIf="mwcAddOnBefore || mwcAddOnBeforeIcon"
        mwc-input-group-slot
        type="addon"
        [icon]="mwcAddOnBeforeIcon"
        [template]="mwcAddOnBefore"
      ></span>
      <span
        *ngIf="isAffix || hasFeedback; else contentTemplate"
        class="mwc-input-affix-wrapper"
        [class.mwc-input-affix-wrapper-disabled]="disabled"
        [class.mwc-input-affix-wrapper-sm]="isSmall"
        [class.mwc-input-affix-wrapper-lg]="isLarge"
        [class.mwc-input-affix-wrapper-focused]="focused"
        [ngClass]="affixInGroupStatusCls"
      >
        <ng-template [ngTemplateOutlet]="affixTemplate"></ng-template>
      </span>
      <span
        *ngIf="mwcAddOnAfter || mwcAddOnAfterIcon"
        mwc-input-group-slot
        type="addon"
        [icon]="mwcAddOnAfterIcon"
        [template]="mwcAddOnAfter"
      ></span>
    </span>
    <ng-template #noAddOnTemplate>
      <ng-template [ngIf]="isAffix" [ngIfElse]="contentTemplate">
        <ng-template [ngTemplateOutlet]="affixTemplate"></ng-template>
      </ng-template>
    </ng-template>
    <ng-template #affixTemplate>
      <span
        *ngIf="mwcPrefix || mwcPrefixIcon"
        mwc-input-group-slot
        type="prefix"
        [icon]="mwcPrefixIcon"
        [template]="mwcPrefix"
      ></span>
      <ng-template [ngTemplateOutlet]="contentTemplate"></ng-template>
      <span
        *ngIf="mwcSuffix || mwcSuffixIcon || isFeedback"
        mwc-input-group-slot
        type="suffix"
        [icon]="mwcSuffixIcon"
        [template]="mwcSuffix"
      >
        <mwc-form-item-feedback-icon *ngIf="isFeedback" [status]="status"></mwc-form-item-feedback-icon>
      </span>
    </ng-template>
    <ng-template #contentTemplate>
      <ng-content></ng-content>
      <span *ngIf="!isAddOn && !isAffix && isFeedback" mwc-input-group-slot type="suffix">
        <mwc-form-item-feedback-icon [status]="status"></mwc-form-item-feedback-icon>
      </span>
    </ng-template>
  `,
  host: {
    '[class.mwc-input-group-compact]': `mwcCompact`,
    '[class.mwc-input-search-enter-button]': `mwcSearch`,
    '[class.mwc-input-search]': `mwcSearch`,
    '[class.mwc-input-search-rtl]': `dir === 'rtl'`,
    '[class.mwc-input-search-sm]': `mwcSearch && isSmall`,
    '[class.mwc-input-search-large]': `mwcSearch && isLarge`,
    '[class.mwc-input-group-wrapper]': `isAddOn`,
    '[class.mwc-input-group-wrapper-rtl]': `dir === 'rtl'`,
    '[class.mwc-input-group-wrapper-lg]': `isAddOn && isLarge`,
    '[class.mwc-input-group-wrapper-sm]': `isAddOn && isSmall`,
    '[class.mwc-input-affix-wrapper]': `isAffix && !isAddOn`,
    '[class.mwc-input-affix-wrapper-rtl]': `dir === 'rtl'`,
    '[class.mwc-input-affix-wrapper-focused]': `isAffix && focused`,
    '[class.mwc-input-affix-wrapper-disabled]': `isAffix && disabled`,
    '[class.mwc-input-affix-wrapper-lg]': `isAffix && !isAddOn && isLarge`,
    '[class.mwc-input-affix-wrapper-sm]': `isAffix && !isAddOn && isSmall`,
    '[class.mwc-input-group]': `!isAffix && !isAddOn`,
    '[class.mwc-input-group-rtl]': `dir === 'rtl'`,
    '[class.mwc-input-group-lg]': `!isAffix && !isAddOn && isLarge`,
    '[class.mwc-input-group-sm]': `!isAffix && !isAddOn && isSmall`
  }
})
export class MwcInputGroupComponent implements AfterContentInit, OnChanges, OnInit, OnDestroy {
  static ngAcceptInputType_mwcSearch: BooleanInput;
  static ngAcceptInputType_mwcCompact: BooleanInput;

  @ContentChildren(MwcInputDirective) listOfMwcInputDirective!: QueryList<MwcInputDirective>;
  @Input() mwcAddOnBeforeIcon?: string | null = null;
  @Input() mwcAddOnAfterIcon?: string | null = null;
  @Input() mwcPrefixIcon?: string | null = null;
  @Input() mwcSuffixIcon?: string | null = null;
  @Input() mwcAddOnBefore?: string | TemplateRef<void>;
  @Input() mwcAddOnAfter?: string | TemplateRef<void>;
  @Input() mwcPrefix?: string | TemplateRef<void>;
  @Input() mwcStatus: MwcStatus = '';
  @Input() mwcSuffix?: string | TemplateRef<void>;
  @Input() mwcSize: MwcSizeLDSType = 'small';
  @Input() @InputBoolean() mwcSearch = false;
  @Input() @InputBoolean() mwcCompact = false;
  isLarge = false;
  isSmall = false;
  isAffix = false;
  isAddOn = false;
  isFeedback = false;
  focused = false;
  disabled = false;
  dir: Direction = 'ltr';
  // status
  prefixCls: string = 'mwc-input';
  affixStatusCls: NgClassInterface = {};
  groupStatusCls: NgClassInterface = {};
  affixInGroupStatusCls: NgClassInterface = {};
  status: MwcValidateStatus = '';
  hasFeedback: boolean = false;
  private destroy$ = new Subject<void>();

  constructor(
    private focusMonitor: FocusMonitor,
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef,
    @Optional() private directionality: Directionality,
    @Optional() private mwcFormStatusService?: MwcFormStatusService,
    @Optional() private mwcFormNoStatusService?: MwcFormNoStatusService
  ) {}

  updateChildrenInputSize(): void {
    if (this.listOfMwcInputDirective) {
      this.listOfMwcInputDirective.forEach(item => (item.mwcSize = this.mwcSize));
    }
  }

  ngOnInit(): void {
    this.mwcFormStatusService?.formStatusChanges
      .pipe(
        distinctUntilChanged((pre, cur) => {
          return pre.status === cur.status && pre.hasFeedback === cur.hasFeedback;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(({ status, hasFeedback }) => {
        this.setStatusStyles(status, hasFeedback);
      });

    this.focusMonitor
      .monitor(this.elementRef, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe(focusOrigin => {
        this.focused = !!focusOrigin;
        this.cdr.markForCheck();
      });

    this.dir = this.directionality.value;
    this.directionality.change?.pipe(takeUntil(this.destroy$)).subscribe((direction: Direction) => {
      this.dir = direction;
    });
  }

  ngAfterContentInit(): void {
    this.updateChildrenInputSize();
    const listOfInputChange$ = this.listOfMwcInputDirective.changes.pipe(startWith(this.listOfMwcInputDirective));
    listOfInputChange$
      .pipe(
        switchMap(list => merge(...[listOfInputChange$, ...list.map((input: MwcInputDirective) => input.disabled$)])),
        mergeMap(() => listOfInputChange$),
        map(list => list.some((input: MwcInputDirective) => input.disabled)),
        takeUntil(this.destroy$)
      )
      .subscribe(disabled => {
        this.disabled = disabled;
        this.cdr.markForCheck();
      });
  }
  ngOnChanges(changes: SimpleChanges): void {
    const {
      mwcSize,
      mwcSuffix,
      mwcPrefix,
      mwcPrefixIcon,
      mwcSuffixIcon,
      mwcAddOnAfter,
      mwcAddOnBefore,
      mwcAddOnAfterIcon,
      mwcAddOnBeforeIcon,
      mwcStatus
    } = changes;
    if (mwcSize) {
      this.updateChildrenInputSize();
      this.isLarge = this.mwcSize === 'large';
      this.isSmall = this.mwcSize === 'small';
    }
    if (mwcSuffix || mwcPrefix || mwcPrefixIcon || mwcSuffixIcon) {
      this.isAffix = !!(this.mwcSuffix || this.mwcPrefix || this.mwcPrefixIcon || this.mwcSuffixIcon);
    }
    if (mwcAddOnAfter || mwcAddOnBefore || mwcAddOnAfterIcon || mwcAddOnBeforeIcon) {
      this.isAddOn = !!(this.mwcAddOnAfter || this.mwcAddOnBefore || this.mwcAddOnAfterIcon || this.mwcAddOnBeforeIcon);
      this.mwcFormNoStatusService?.noFormStatus?.next(this.isAddOn);
    }
    if (mwcStatus) {
      this.setStatusStyles(this.mwcStatus, this.hasFeedback);
    }
  }
  ngOnDestroy(): void {
    this.focusMonitor.stopMonitoring(this.elementRef);
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setStatusStyles(status: MwcValidateStatus, hasFeedback: boolean): void {
    // set inner status
    this.status = status;
    this.hasFeedback = hasFeedback;
    this.isFeedback = !!status && hasFeedback;
    const baseAffix = !!(this.mwcSuffix || this.mwcPrefix || this.mwcPrefixIcon || this.mwcSuffixIcon);
    this.isAffix = baseAffix || (!this.isAddOn && hasFeedback);
    this.affixInGroupStatusCls =
      this.isAffix || this.isFeedback
        ? (this.affixStatusCls = getStatusClassNames(`${this.prefixCls}-affix-wrapper`, status, hasFeedback))
        : {};
    this.cdr.markForCheck();
    // render status if mwcStatus is set
    this.affixStatusCls = getStatusClassNames(
      `${this.prefixCls}-affix-wrapper`,
      this.isAddOn ? '' : status,
      this.isAddOn ? false : hasFeedback
    );
    this.groupStatusCls = getStatusClassNames(
      `${this.prefixCls}-group-wrapper`,
      this.isAddOn ? status : '',
      this.isAddOn ? hasFeedback : false
    );
    const statusCls = {
      ...this.affixStatusCls,
      ...this.groupStatusCls
    };
    Object.keys(statusCls).forEach(status => {
      if (statusCls[status]) {
        this.renderer.addClass(this.elementRef.nativeElement, status);
      } else {
        this.renderer.removeClass(this.elementRef.nativeElement, status);
      }
    });
  }
}
